// src/components/admin/AdminReports.jsx
// Reports & Exports — Phase 6

import { useState } from 'react';
import { getAccessToken } from '../../utils/api';

const API = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// ── Download helper — triggers browser file download ─────────
async function downloadCSV(endpoint, filename, setLoading) {
  setLoading(true);
  try {
    const res = await fetch(`${API}${endpoint}`, {
      headers: { Authorization: `Bearer ${getAccessToken()}` },
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    alert(`Export failed: ${e.message}`);
  } finally {
    setLoading(false);
  }
}

// ── Report Card ───────────────────────────────────────────────
function ReportCard({ icon, title, description, filename, endpoint, rows, columns, color = 'var(--gold)' }) {
  const [loading, setLoading] = useState(false);

  const today    = new Date().toISOString().slice(0, 10);
  const fullName = filename.replace('DATE', today);

  return (
    <div style={{ background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '22px 24px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${color}, transparent 70%)` }}/>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: `${color}15`, border: `1px solid ${color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)', marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--gray)', lineHeight: 1.6 }}>{description}</div>
        </div>
      </div>

      {/* Meta */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', color: 'var(--gray)', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', padding: '3px 10px', borderRadius: 20 }}>
          CSV
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', color: 'var(--gray)', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', padding: '3px 10px', borderRadius: 20 }}>
          {columns} columns
        </span>
        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray)', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', padding: '3px 10px', borderRadius: 20, fontFamily: 'monospace' }}>
          {fullName}
        </span>
      </div>

      <button
        onClick={() => downloadCSV(endpoint, fullName, setLoading)}
        disabled={loading}
        style={{
          width: '100%', padding: '11px 0',
          background: loading ? 'rgba(255,255,255,.04)' : `${color}12`,
          border: `1px solid ${color}${loading ? '20' : '35'}`,
          color: loading ? 'var(--gray)' : color,
          fontSize: 12, fontWeight: 700, letterSpacing: '.06em',
          textTransform: 'uppercase', cursor: loading ? 'not-allowed' : 'pointer',
          fontFamily: "'Inter',sans-serif", borderRadius: 'var(--radius)',
          transition: 'all .15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {loading ? (
          <>
            <span style={{ display: 'inline-block', width: 12, height: 12, border: `2px solid ${color}40`, borderTop: `2px solid ${color}`, borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
            Exporting…
          </>
        ) : (
          <>↓ Download CSV</>
        )}
      </button>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
export default function AdminReports() {
  const reports = [
    {
      icon:        '👤',
      title:       'User Report',
      description: 'All registered users with plan status, trade count, timezone, account type, and join date.',
      endpoint:    '/admin/reports/users',
      filename:    'kfx-users-DATE.csv',
      columns:     13,
      color:       'var(--gold)',
    },
    {
      icon:        '💰',
      title:       'Revenue Report',
      description: 'All referral subscriptions with referrer/referee details, plan amounts, commissions, and conversion dates.',
      endpoint:    '/admin/reports/revenue',
      filename:    'kfx-revenue-DATE.csv',
      columns:     11,
      color:       'var(--green)',
    },
    {
      icon:        '📈',
      title:       'Trades Report',
      description: 'Full platform trade log — every trade across all users with entry, SL, TP, PnL, R:R, strategy, emotion, and grade.',
      endpoint:    '/admin/reports/trades',
      filename:    'kfx-trades-DATE.csv',
      columns:     18,
      color:       'var(--blue)',
    },
  ];

  return (
    <div style={{ padding: '14px 18px 60px', animation: 'pageIn .3s ease' }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)', marginBottom: 4 }}>Reports & Exports</div>
        <div style={{ fontSize: 12, color: 'var(--gray)' }}>Download platform data as CSV files. All exports reflect live data at time of download.</div>
      </div>

      {/* Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        {reports.map(r => <ReportCard key={r.title} {...r}/>)}
      </div>

      {/* Note */}
      <div style={{ marginTop: 20, padding: '12px 16px', background: 'rgba(212,168,67,.05)', border: '1px solid rgba(212,168,67,.12)', borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--gray)', lineHeight: 1.7 }}>
        <span style={{ color: 'var(--gold)', fontWeight: 700 }}>Note: </span>
        CSV files open in Excel, Google Sheets, or any spreadsheet tool. Dates are in ISO format (YYYY-MM-DD). Monetary values are in USD. All exports include all records with no row limit.
      </div>

      {/* Spin animation */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
